import { FC } from "react";
import { Alert, Form, Switch } from "antd";
import _t from "../../../lang/translate";
import LeasingInsuranceActiveSwitch from "./leasingInsuranceActiveSwitch";
import InputPrice from "../../../components/form/inputPrice";
import LeasingInsuranceRoadAssistance from "./leasingInsuranceRoadAssistance";
import LeasingInsuranceGlassDamage from "./leasingInsuranceGlassDamage";
import InsurancePriceField from "./insurancePriceField";
import LeasingStore from "../../../contexts/leasingStore";
import { LeasingDealType } from "./calc/leasingCalcTypes";
import EnumRadio from "../../../components/form/EnumRadio";
import { getSavedEnums } from "../../../services/authService";
import { capitalize, normalize } from "../../../utilities/stringExtraFunctions";

const LeasingInsurance: FC = () => {
  const { leasingCase } = LeasingStore.useContainer();
  const { inputLocked, readonly } = leasingCase.flags;
  const disabled = inputLocked || readonly;

  const blacklistedModel = getBlacklistedModel(leasingCase.vehicle?.brand || "")
  const blacklistedBrand = getBlacklistedBrand(leasingCase.vehicle?.brand || "")
  const shouldUpdate = (curr?: LeasingDealType, next?: LeasingDealType) =>
    curr?.insuranceInput?.active !== next?.insuranceInput?.active ||
    curr?.insuranceOutput?.insurable !== next?.insuranceOutput?.insurable;

  const trygSource = getSavedEnums()?.leasingCase?.insuranceSource?.find(item => item.title === 'Tryg');
  return (
    <Form.Item noStyle shouldUpdate={shouldUpdate}>
      {({ getFieldsValue }) => {
        const deal: LeasingDealType = getFieldsValue(true);
        const isActive = deal?.insuranceInput?.active;
        const vehiclePrice = deal.input.vehiclePrice;
        const insurable = deal?.insuranceOutput?.insurable;
        const isTryg = deal.insuranceInput.source === trygSource?.id

        return (
          <>
            <LeasingInsuranceActiveSwitch disabled={disabled} />
            {(isActive) && (
              <>
                <Form.Item label={_t("source")} name={['insuranceInput', 'source']}
                  help={
                    <>
                      {isTryg && blacklistedModel && (
                        <small className="text-warning">
                          {blacklistedModel}
                        </small>
                      )}
                      {isTryg && blacklistedBrand && (
                        <small className="text-danger">
                          {blacklistedBrand}
                        </small>
                      )}
                    </>
                  }
                >
                  <EnumRadio nameKey={['leasingCase', 'insuranceSource']} disabled={disabled} />
                </Form.Item>
                <Form.Item label={_t("insurance_sum")} name={["insuranceInput", "vehiclePrice"]} className="mb-05">
                  <InsurancePriceField isActive={isActive} vehiclePrice={vehiclePrice} disabled={disabled} />
                </Form.Item>
                <Form.Item label={_t("own_risk")} name={["insuranceOutput", "ownRisk"]} className="mb-05">
                  <InputPrice suffix={_t("dkk_price_short")} disabled />
                </Form.Item>
                {!isTryg && (
                  <Form.Item
                    label={_t("is_over_26")}
                    name={["insuranceInput", "isUserOver26"]}
                    className="mb-05"
                    valuePropName="checked"
                  >
                    <Switch size="small" disabled={disabled} />
                  </Form.Item>
                )}
                <LeasingInsuranceRoadAssistance disabled={disabled} />
                <LeasingInsuranceGlassDamage disabled={disabled} />
                {!insurable && <Alert type="warning" banner message={_t("msg.not_insurable")} className="mb-1" />}
              </>
            )}
          </>
        );
      }}
    </Form.Item>
  );
};

export default LeasingInsurance;

const blacklistedBrands = [
  'porsche',
  'maserati',
  'lamborghini',
  'bentley',
  'land rover',
  'aston martin',
  'ferrari',
  'rolls royce',
  'mclaren',
  'jaguar',
  'corvette',
  'cadillac',
  'lotus'
]
const brandsWithBlacklistedModels: Record<string, string> = {
  'bmw': 'BMW M-modeller kan ikke forsikres ved Tryg',
  'mercedes benz': 'Mercedes-Benz AMG- og G-modeller kan ikke forsikres ved Tryg',
  'audi': 'Audi R- og RS-modeller kan ikke forsikres ved Tryg'
}
function getBlacklistedBrand(brand: string) {
  const normalizedBrand = normalize(brand)
  const blacklistedBrand = blacklistedBrands.find(brand => brand === normalizedBrand)
  if (blacklistedBrand) {
    return capitalize(blacklistedBrand) + " kan ikke forsikres ved Tryg."
  }
  return null
}
function getBlacklistedModel(brand: string) {
  const normalizedBrand = normalize(brand)
  return brandsWithBlacklistedModels?.[normalizedBrand]
}
